import { useState } from "react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { CheckCircle2, AlertTriangle, MessageSquare, Loader2, Building2, Sun, HelpCircle, Mic } from "lucide-react";

const QQOCP_LABELS = { qui: "Qui ?", quoi: "Quoi ?", ou: "Où ?", quand: "Quand ?", comment: "Comment ?", pourquoi: "Pourquoi ?" };

function ReviewControls({ portfolio, onChanged }) {
  const [status, setStatus] = useState(portfolio.validation_status === "pending" ? "" : portfolio.validation_status);
  const [comment, setComment] = useState(portfolio.teacher_comment || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!status) { toast.error("Choisis un statut (Validé ou À revoir)."); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/pfmp-portfolio/${portfolio.id}/review`, { status, comment });
      toast.success("Évaluation enregistrée ✓");
      onChanged && onChanged();
    } catch {
      toast.error("Enregistrement impossible");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 space-y-2.5 rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="flex gap-2">
        <button type="button" onClick={() => setStatus("validated")} data-testid={`pfmp-portfolio-validate-${portfolio.id}`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${status === "validated" ? "bg-emerald-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-emerald-300"}`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Validé
        </button>
        <button type="button" onClick={() => setStatus("to_revise")} data-testid={`pfmp-portfolio-revise-${portfolio.id}`}
          className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${status === "to_revise" ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-300"}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> À revoir
        </button>
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Commentaire pour l'élève (facultatif)…"
        rows={2}
        data-testid={`pfmp-portfolio-comment-${portfolio.id}`}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
      />
      <button onClick={save} disabled={busy} data-testid={`pfmp-portfolio-save-${portfolio.id}`}
        className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white rounded-full px-4 py-2 font-bold text-xs transition-colors">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Enregistrer
      </button>
    </div>
  );
}

export default function PfmpPortfolioCard({ portfolio, reviewMode = false, onChanged }) {
  const status = portfolio.validation_status;
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid={`pfmp-portfolio-card-${portfolio.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="font-bold text-slate-800">{portfolio.pfmp_title}</p>
        {status === "validated" && <span className="inline-flex items-center gap-1 text-xs font-extrabold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full"><CheckCircle2 className="h-3.5 w-3.5" /> Validé</span>}
        {status === "to_revise" && <span className="inline-flex items-center gap-1 text-xs font-extrabold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full"><AlertTriangle className="h-3.5 w-3.5" /> À revoir</span>}
        {status === "pending" && <span className="inline-flex items-center gap-1 text-xs font-extrabold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">En attente</span>}
      </div>

      {portfolio.company_description && (
        <div className="mb-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-0.5 inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> Entreprise</p>
          <p className="text-sm text-slate-700">{portfolio.company_description}</p>
        </div>
      )}
      {portfolio.typical_day && (
        <div className="mb-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-0.5 inline-flex items-center gap-1"><Sun className="h-3 w-3" /> Journée type</p>
          <p className="text-sm text-slate-700">{portfolio.typical_day}</p>
        </div>
      )}
      {Object.values(portfolio.qqocp || {}).some(Boolean) && (
        <div className="mb-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1 inline-flex items-center gap-1"><HelpCircle className="h-3 w-3" /> Tâche professionnelle (QQOQCP)</p>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {Object.entries(QQOCP_LABELS).map(([k, label]) => portfolio.qqocp?.[k] && (
              <p key={k} className="text-xs text-slate-600"><span className="font-bold">{label}</span> {portfolio.qqocp[k]}</p>
            ))}
          </div>
        </div>
      )}
      {portfolio.photos?.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5 mb-2">
          {portfolio.photos.map((p) => (
            <a key={p.id} href={fileUrl(p.id)} target="_blank" rel="noreferrer"><img src={fileUrl(p.id)} alt={p.name} className="h-16 w-full object-cover rounded-lg border border-slate-200" /></a>
          ))}
        </div>
      )}
      {portfolio.audio && (
        <div className="mb-2">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1 inline-flex items-center gap-1"><Mic className="h-3 w-3" /> Présentation orale</p>
          <audio src={fileUrl(portfolio.audio.id)} controls className="w-full h-9" />
        </div>
      )}
      {!reviewMode && portfolio.teacher_comment && (
        <div className="mt-2 rounded-xl bg-orange-50 border border-orange-100 p-2.5">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-orange-700 mb-1 flex items-center gap-1.5"><MessageSquare className="h-3 w-3" /> Commentaire du professeur</p>
          <p className="text-sm text-slate-700">{portfolio.teacher_comment}</p>
        </div>
      )}

      {reviewMode && <ReviewControls portfolio={portfolio} onChanged={onChanged} />}
    </div>
  );
}
